import type { SoldSegment } from "@/lib/sold-archive.functions";

export interface SoldDetailInput {
  title: string;
  price: number | null;
  category: string;
  soldAt: string;
  daysOnMarket: number | null;
  firstSeenAt: string | null;
  image: string | null;
  brand: string | null;
  year: number | null;
}

export interface SoldDetail {
  headline: string;
  lead: string;
  facts: Array<{ label: string; value: string }>;
  priceContext: string[];
  demand: string;
  checklist: { title: string; items: string[] };
  buyerAdvice: string[];
  sellerAdvice: string[];
  faq: Array<{ q: string; a: string }>;
  wordCount: number;
}

const sek = (n: number | null) =>
  n == null ? "okänt" : new Intl.NumberFormat("sv-SE").format(n) + " kr";

const CHECKLISTS: Record<string, string[]> = {
  husbil: [
    "Fukt i golvvinkel, hattkupé och runt takluckor — mätt med stickmätare på minst tolv punkter.",
    "Gasolsystemet täthetsprovat och regulatorns datum kontrollerat (byts vart tionde år).",
    "Kamrem/kamkedja, servicebok och senaste oljebyte mot mätarställning.",
    "Kylskåpets drift på både 12 V, 230 V och gas.",
    "Rostkontroll på ramen bakom bakhjulen och vid dragkroken.",
    "Batteribankens verkliga kapacitet, inte bara vilospänningen.",
  ],
  husvagn: [
    "Fukt i framvägg, under fönsterlister och i golvet vid ingången.",
    "Chassinummer mot registreringsbevis och kontroll av totalvikt mot dragbilen.",
    "Hjullager, bromsar och däckens DOT-datum (gummi åldras även utan mil).",
    "Gasollådans ventilation och slangarnas datum.",
    "Markisens duk och att stödbenen går att veva ut hela vägen.",
    "Tak- och skarvtätningar — mjuk fogmassa är rätt, spröd och sprucken är dyrt.",
  ],
};

export function buildSoldDetail(
  v: SoldDetailInput,
  categorySegment: SoldSegment | null,
  brandSegment: SoldSegment | null,
  totalSold: number,
): SoldDetail {
  const catLabel = v.category === "husvagn" ? "husvagn" : v.category === "husbil" ? "husbil" : v.category;
  const headline = `${v.title} — såld ${v.soldAt}`;

  const lead =
    `${v.title} såldes hos AK Husbil & Fritid i Kalix den ${v.soldAt}` +
    (v.price ? ` för ${sek(v.price)}` : "") +
    (v.daysOnMarket != null ? ` efter ${v.daysOnMarket} dagar i vårt lager` : "") +
    `. Den här sidan är en del av vårt öppna arkiv över verkliga slutpriser: i stället för att gissa vad en ` +
    `begagnad ${catLabel} är värd kan du se vad exemplar som det här faktiskt betalades för, hur snabbt de ` +
    `omsattes och hur priset låg jämfört med resten av lagret. Fordonet är sålt och går inte att köpa längre — ` +
    `siffrorna finns kvar eftersom de är användbara både när du ska köpa och när du ska sätta pris på ditt eget.`;

  const facts: Array<{ label: string; value: string }> = [
    { label: "Fordonstyp", value: catLabel },
    { label: "Slutpris", value: sek(v.price) },
    { label: "Såld", value: v.soldAt },
  ];
  if (v.brand) facts.push({ label: "Märke", value: v.brand });
  if (v.year) facts.push({ label: "Årsmodell", value: String(v.year) });
  if (v.daysOnMarket != null) facts.push({ label: "Dagar till försäljning", value: `${v.daysOnMarket} dagar` });
  if (v.firstSeenAt) facts.push({ label: "Lades ut", value: v.firstSeenAt.slice(0, 10) });

  const priceContext: string[] = [];
  if (v.price && categorySegment?.medianPrice) {
    const diff = v.price - categorySegment.medianPrice;
    const pct = Math.round((diff / categorySegment.medianPrice) * 100);
    priceContext.push(
      `Medianpriset för alla ${catLabel}ar vi sålt är ${sek(categorySegment.medianPrice)} (${categorySegment.count} fordon). ` +
        (Math.abs(pct) < 5
          ? `Det här fordonet landade praktiskt taget på medianen, vilket brukar betyda att priset var rätt satt från start.`
          : pct > 0
          ? `Det här exemplaret gick ${pct} % över medianen — normalt för nyare årsmodell, låg mätarställning eller tung utrustning.`
          : `Det här exemplaret gick ${Math.abs(pct)} % under medianen, vilket typiskt beror på ålder, mätarställning eller att något behövde åtgärdas.`),
    );
  }
  if (v.price && brandSegment?.medianPrice && brandSegment.count > 1 && v.brand) {
    priceContext.push(
      `Inom ${v.brand} ligger vår median på ${sek(brandSegment.medianPrice)} över ${brandSegment.count} sålda exemplar` +
        (brandSegment.avgDays != null ? `, med ${brandSegment.avgDays} dagar som snittid till försäljning` : "") +
        `. Använd den siffran som referens hellre än begärda priser i annonser — begärt pris och betalt pris är sällan samma sak.`,
    );
  }
  priceContext.push(
    `Underlaget bakom siffrorna är ${totalSold} sålda fordon som loggats automatiskt när de försvann ur lagret. ` +
      `Det är ett litet men ärligt underlag: vi mäter våra egna affärer, inte hela marknaden, och redovisar antalet så att du själv kan bedöma osäkerheten.`,
  );

  const demand =
    v.daysOnMarket == null
      ? `Vi har ingen tillförlitlig liggetid för det här fordonet, eftersom det fanns i lagret redan när mätningen startade.`
      : v.daysOnMarket <= 14
      ? `${v.daysOnMarket} dagar är snabbt. Fordon som går den här vägen är nästan alltid rätt prissatta från början och har en efterfrågad planlösning. Ska du köpa något liknande behöver du vara beredd att bestämma dig samma vecka som annonsen dyker upp.`
      : v.daysOnMarket <= 45
      ? `${v.daysOnMarket} dagar är en normal liggetid för den här prisklassen. Det finns utrymme att boka visning, göra en ordentlig fuktkontroll och räkna på finansiering utan att förlora affären.`
      : `${v.daysOnMarket} dagar är en lång liggetid. Det betyder oftast antingen ett smalt kundunderlag (stor, dyr eller ovanlig planlösning) eller ett startpris över marknadsnivån. Som köpare har du förhandlingsutrymme på fordon som legat så länge.`;

  const items = CHECKLISTS[catLabel] ?? CHECKLISTS.husbil;
  const checklist = {
    title: `Vad vi kontrollerade på det här fordonet innan det såldes`,
    items,
  };

  const buyerAdvice = [
    `Vill du hitta något liknande: jämför alltid slutpris mot begärt pris. Skillnaden är ofta 3–8 % på ett fordon som legat mer än en månad.`,
    `Ta med årsmodell och mätarställning i bedömningen — ett ${v.year ?? "äldre"} exemplar med hög mätarställning men komplett servicehistorik är oftast ett tryggare köp än ett lågmilat utan papper.`,
    `Begär alltid fuktprotokoll med datum och mätvärden, inte bara ordet "fukttestad".`,
  ];

  const sellerAdvice = [
    `Sätt priset mot slutpriser, inte mot de dyraste annonserna som ligger kvar osålda.`,
    `Fotografera i dagsljus, med tömda skåp och utfälld markis — annonser med kompletta bilder får fler samtal första veckan, och första veckan avgör liggetiden.`,
    `Redovisa det som behöver göras. Vi säljer snabbare på ärliga annonser än på putsade.`,
  ];

  const faq = [
    {
      q: `Går ${v.title} att köpa fortfarande?`,
      a: `Nej, fordonet är sålt${v.soldAt ? ` (${v.soldAt})` : ""}. Sidan finns kvar som prisreferens. Aktuellt lager hittar du under husbilar och husvagnar till salu.`,
    },
    {
      q: `Är slutpriset ${sek(v.price)} vad köparen faktiskt betalade?`,
      a: `Det är det publicerade priset när fordonet lämnade lagret. Eventuell inbytesaffär eller tillval som garanti och transport ligger utanför siffran.`,
    },
    {
      q: `Kan jag få hjälp att värdera min egen ${catLabel}?`,
      a: `Ja. Ring 070-862 97 43 eller mejla info@akhusbilfritid.se med regnr och bilder, så återkommer vi med en värdering baserad på samma data som den här sidan bygger på.`,
    },
    {
      q: `Hur ofta uppdateras arkivet?`,
      a: `Lagret läses av automatiskt varje timme. Ett fordon som försvinner och inte kommer tillbaka räknas som sålt och hamnar i arkivet samma dag.`,
    },
  ];

  const allText = [
    lead,
    ...priceContext,
    demand,
    ...items,
    ...buyerAdvice,
    ...sellerAdvice,
    ...faq.flatMap((f) => [f.q, f.a]),
  ].join(" ");
  const wordCount = allText.split(/\s+/).filter(Boolean).length;

  return { headline, lead, facts, priceContext, demand, checklist, buyerAdvice, sellerAdvice, faq, wordCount };
}

const BRANDS = [
  "Rapido", "Kabe", "Adria", "Hobby", "Knaus", "Dethleffs", "Cabby", "Polar",
  "Solifer", "Hymer", "Bürstner", "Sunlight", "Carado", "Weinsberg",
  "Fendt", "Tabbert", "Elddis", "Chausson", "Challenger", "Benimar", "Laika",
];

export function brandFromTitle(title: string): string | null {
  const t = (title || "").toLowerCase();
  return BRANDS.find((b) => t.includes(b.toLowerCase())) ?? null;
}

export function yearFromTitle(title: string): number | null {
  const m = (title || "").match(/\b(19[89]\d|20[0-4]\d)\b/);
  return m ? Number(m[1]) : null;
}
